import { useEffect, useState } from "react";
import { ref, update } from "firebase/database";
import { getData } from "country-list";
import { auth, database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { call, useRecord } from "../utils/appData";
import WorkShell, { State } from "../components/AppPage";
export function BankPayoutForm({ externalLinksInNewTab = false } = {}) {
  const { currentUser, refreshUser } = useUser();
  const uid = currentUser?.uid;
  const live = useRecord(uid ? `Users/${uid}` : null);
  const user = live.data || currentUser;
  const [method, setMethod] = useState("paystack");
  const [country, setCountry] = useState("ZA");
  const [banks, setBanks] = useState([]);
  const [bank, setBank] = useState("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const openPaymentLink = url => {
    if (externalLinksInNewTab) setExternalUrl(url);
    else window.location.assign(url);
  };
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (user?.payoutMethod) setMethod(user.payoutMethod);
    if (user?.sellerCountry) setCountry(user.sellerCountry);
  }, [user?.payoutMethod, user?.sellerCountry]);
  useEffect(() => {
    let active = true;
    call("getPaystackBanks")
      .then((r) => {
        if (active) setBanks(r.data || []);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, []);
  const save = async (patch) => {
    if (auth.currentUser?.uid !== uid)
      throw new Error("Your sign-in has changed. Please reload.");
    await update(ref(database, `Users/${uid}`), patch);
    await refreshUser();
  };
  const run = async (action) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  async function connect(e) {
    e.preventDefault();
    await run(async () => {
      if (method === "stripe") {
        await call("createConnectedAccount", {
          userId: uid,
          email: user.email,
          country,
        });
        await save({ payoutMethod: "stripe", sellerCountry: country });
        const r = await call("createConnectOnboardingLink", {
          userId: uid,
          refreshUrl: `${window.location.origin}/#/bank-payouts`,
          returnUrl: `${window.location.origin}/#/bank-payouts`,
        });
        openPaymentLink(r.url);
      } else {
        const r = await call("createPaystackSubaccount", {
          userId: uid,
          business_name: user.username || user.name || "My Business",
          settlement_bank: bank,
          account_number: number.trim(),
          percentage_charge: 0,
          email: user.email,
        });
        const code = r.data?.subaccount_code;
        if (!code)
          throw new Error(r.message || "Unable to verify bank account.");
        await save({
          paystackSubaccountCode: code,
          payoutMethod: "paystack",
          sellerCountry: "ZA",
          sellerOnboardingStatus: "complete",
          sellerCanReceivePayments: true,
          paystackBank: bank,
          paystackBankName: banks.find((b) => b.code === bank)?.name || "",
          paystackAccountNumber: number.trim(),
        });
        setNumber("");
        setNotice("Bank details saved.");
      }
    });
  }
  async function detach() {
    if (
      !window.confirm(
        "Detach your payout account? You will need to set up payouts again before receiving new payments.",
      )
    )
      return;
    await run(async () => {
      if (user.paystackSubaccountCode)
        await call("markPaystackSubaccountDetached", {
          subaccountCode: user.paystackSubaccountCode,
        });
      await save({
        paystackSubaccountCode: null,
        stripeConnectedAccountId: null,
        paystackBank: null,
        paystackBankName: null,
        paystackAccountNumber: null,
        payoutMethod: null,
        sellerOnboardingStatus: "incomplete",
        sellerCanReceivePayments: false,
      });
      setNotice("Account detached. Choose a payout provider below.");
    });
  }
  const connected = Boolean(
    user?.paystackSubaccountCode || user?.stripeConnectedAccountId,
  );
  return (
    <>
      <State {...live} />
      <section className="zy-card mb-[18px] rounded-[20px] border border-slate-200 bg-white p-[22px] dark:border-[#363e46] dark:bg-[#23272c] [&_h2]:mb-3 [&_h2]:text-[19px] [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:text-[17px] [&_h3]:font-bold">
        <h2>
          {user?.sellerCanReceivePayments
            ? "Payouts ready"
            : "Set up your payouts"}
        </h2>
        <p className="zy-muted text-sm text-slate-500 dark:text-[#a4b0bc]">
          {user?.payoutMethod === "paystack"
            ? `${user.paystackBankName || "Paystack"} ${user.paystackAccountNumber ? "•••• " + user.paystackAccountNumber.slice(-4) : ""}`
            : user?.stripeConnectedAccountId
              ? "Stripe connected account"
              : "Add your bank details to receive payments."}
        </p>
        {connected && (
          <div className="zy-actions flex flex-wrap items-center gap-3 mt-5">
            {user?.stripeConnectedAccountId && (
              <>
                <button
                  disabled={busy}
                  className="zy-button inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[14px] px-[17px] py-[11px] text-sm font-semibold disabled:cursor-wait disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] border border-slate-200 bg-white dark:border-[#363e46] dark:bg-[#23272c] secondary"
                  onClick={() =>
                    run(async () => {
                      await call("checkConnectAccountStatus", { userId: uid });
                      await refreshUser();
                      setNotice("Account status refreshed.");
                    })
                  }
                >
                  Refresh status
                </button>
                <button
                  disabled={busy}
                  className="zy-button inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[14px] px-[17px] py-[11px] text-sm font-semibold disabled:cursor-wait disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] border border-slate-200 bg-white dark:border-[#363e46] dark:bg-[#23272c] secondary"
                  onClick={() =>
                    run(async () => {
                      const r = await call("getConnectDashboardLink", {
                        userId: uid,
                      });
                      openPaymentLink(r.url);
                    })
                  }
                >
                  Stripe dashboard
                </button>
              </>
            )}
            <button
              disabled={busy}
              className="zy-button inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[14px] px-[17px] py-[11px] text-sm font-semibold disabled:cursor-wait disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] border border-slate-200 bg-white dark:border-[#363e46] dark:bg-[#23272c] secondary"
              onClick={detach}
            >
              Detach / switch provider
            </button>
          </div>
        )}
      </section>
      <form
        className="zy-card mb-[18px] rounded-[20px] border border-slate-200 bg-white p-[22px] dark:border-[#363e46] dark:bg-[#23272c] [&_h2]:mb-3 [&_h2]:text-[19px] [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:text-[17px] [&_h3]:font-bold zy-form grid max-w-[700px] gap-4 [&_label]:text-sm [&_label]:font-semibold"
        onSubmit={connect}
      >
        <h2>{connected ? "Update bank details" : "Choose payout method"}</h2>
        <label>
          Provider
          <select
            className="zy-input mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] dark:border-[#363e46] dark:bg-[#23272c]"
            value={method}
            disabled={connected || busy}
            onChange={(e) => setMethod(e.target.value)}
          >
            <option value="paystack">Paystack — South Africa</option>
            <option value="stripe">Stripe — supported countries</option>
          </select>
        </label>
        {method === "paystack" ? (
          <>
            <label>
              Search banks
              <input
                className="zy-input mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] dark:border-[#363e46] dark:bg-[#23272c]"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <label>
              Bank
              <select
                required
                className="zy-input mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] dark:border-[#363e46] dark:bg-[#23272c]"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
              >
                <option value="">Select a bank</option>
                {banks
                  .filter(
                    (b) =>
                      b.name.toLowerCase().includes(search.toLowerCase()) ||
                      b.code === bank,
                  )
                  .map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Account number
              <input
                required
                inputMode="numeric"
                autoComplete="off"
                className="zy-input mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] dark:border-[#363e46] dark:bg-[#23272c]"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                maxLength={30}
              />
            </label>
          </>
        ) : (
          <label>
            Country
            <select
              className="zy-input mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] dark:border-[#363e46] dark:bg-[#23272c]"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {getData()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option value={c.code} key={c.code}>
                    {c.name}
                  </option>
                ))}
            </select>
            <span className="zy-muted text-sm text-slate-500 dark:text-[#a4b0bc]">
              Stripe checks whether your country is eligible during setup.
            </span>
          </label>
        )}
        <button
          disabled={busy || live.loading}
          className="zy-button inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[14px] px-[17px] py-[11px] text-sm font-semibold disabled:cursor-wait disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] bg-[#327691] text-white"
        >
          {busy
            ? "Please wait…"
            : method === "paystack"
              ? "Save bank details"
              : "Continue with Stripe"}
        </button>
      </form>
      {error && (
        <p
          className="zy-error my-3 rounded-[10px] bg-red-100 p-3 text-red-700 dark:bg-red-950 dark:text-red-200"
          role="alert"
        >
          {error}
        </p>
      )}
      {externalUrl && (
        <div className="my-4 rounded-xl bg-[#327691]/10 p-4 text-sm">
          <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center rounded-full bg-[#327691] px-5 py-2 font-bold text-white">Open secure Stripe setup</a>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Finish in the new tab, then return here. Your job form stays open.</p>
        </div>
      )}
      {notice && <p role="status">{notice}</p>}
    </>
  );
}
export default function BankPayouts() {
  return (
    <WorkShell title="Bank & Payouts">
      <BankPayoutForm />
    </WorkShell>
  );
}
